import DSBox from "views/system-design/DSBox";
import SimpleSwitch from "../../../../components/Widgets/SimpleSwitch";
import SimpleBar from "../../../../components/Widgets/SimpleBar";
import SimpleDropdown from "../../../../components/Widgets/SimpleDropdown";
import SimpleTimePicker from "../../../../components/Widgets/SimpleTimePicker";
import { NightModeAppearance } from "../../../../Types";
import {
  NIGHT_MODE,
  SCREEN_STATE,
  SettingsSectionProps,
  settingValue,
} from "./constants";

type NightModeTimes = { from: string; until: string };

const NIGHT_MODE_APPEARANCE_OPTIONS = Object.values(NightModeAppearance);

/**
 * Display settings for a profile — informed by DeviceModal.tsx's Display
 * section (lines 1111-1335 at last read), not extracted from it. Left out:
 *  - nightModeAppearance's and screenState's version gates (>= 615.3 and
 *    >= 590 respectively) — always shown, same reasoning as the other two
 *    sections: no single device version to check for a profile.
 * Kept: nightModeTimes only shows when nightMode is "SCHEDULE" — that's a
 * genuine field interdependency (DeviceModal's own `schedualOn`, derived the
 * same way in useDeviceModal.ts), not a device-specific quirk, so it carries
 * over unchanged.
 */
const DisplaySettings = ({ values, onChange }: SettingsSectionProps) => {
  const nightModeTimes = settingValue<NightModeTimes>(values, "nightModeTimes");
  const scheduleOn = values.nightMode === "SCHEDULE";

  return (
    <DSBox>
      <DSBox title="Screen brightness">
        <SimpleBar
          title="Brightness:"
          max={10}
          default={settingValue<number>(values, "brightness")}
          onChange={(e) => onChange("brightness", Number(e.target.value))}
        />
      </DSBox>
      <DSBox title="Night mode: auto / on schedule / off" sx={{ mt: 1 }}>
        <SimpleDropdown
          title="Night mode: "
          list={NIGHT_MODE}
          default={settingValue<string>(values, "nightMode")}
          onSelect={(s) => onChange("nightMode", s)}
        />
      </DSBox>
      {scheduleOn && (
        <DSBox title="Night Mode Times" sx={{ mt: 1, mb: 1 }}>
          <DSBox sx={{ display: "flex", flexWrap: "wrap", gap: 2, pl: "20px" }}>
            <SimpleTimePicker
              title="From:"
              default={nightModeTimes.from}
              onChange={(e) =>
                onChange("nightModeTimes", {
                  from: e.target.value,
                  until: nightModeTimes.until,
                })
              }
            />
            <SimpleTimePicker
              title="Until:"
              default={nightModeTimes.until}
              onChange={(e) =>
                onChange("nightModeTimes", {
                  from: nightModeTimes.from,
                  until: e.target.value,
                })
              }
            />
          </DSBox>
        </DSBox>
      )}
      <DSBox
        title="Night mode appearance: red theme / dimmed red / screen off"
        sx={{ mt: 1 }}
      >
        <SimpleDropdown
          title="Night Mode Appearance: "
          list={NIGHT_MODE_APPEARANCE_OPTIONS}
          default={settingValue<string>(values, "nightModeAppearance")}
          onSelect={(s) => onChange("nightModeAppearance", s)}
        />
      </DSBox>
      <DSBox title="Screen state: off / default / night mode" sx={{ mt: 1 }}>
        <SimpleDropdown
          title="Screen State: "
          list={SCREEN_STATE}
          default={settingValue<string>(values, "screenState")}
          onSelect={(s) => onChange("screenState", s)}
        />
      </DSBox>
      <DSBox
        title="Show dials on/off"
        sx={{ display: "flex", ml: "20px", mt: 1 }}
      >
        <SimpleSwitch
          title="Show Dials"
          on={undefined}
          default={settingValue<boolean>(values, "showDials")}
          onClick={(on) => onChange("showDials", on)}
        />
      </DSBox>
    </DSBox>
  );
};

export default DisplaySettings;
